import { X, ExternalLink, Clock, User, GitCommit, AlertCircle, FileText, MessageSquare, BookOpen, CheckCircle, Shield } from 'lucide-react';

export default function DetailModal({ nodeData, onClose }) {
  if (!nodeData) return null;

  // Helper to format URLs - returns null if no real URL available
  const getExternalUrl = (url) => {
    if (!url || url.startsWith('[SAMPLE]') || url.includes('example.test')) {
      return null;
    }
    return url;
  };

  // Helper to format email/author - strips [SAMPLE] prefix
  const formatAuthor = (author) => {
    if (!author) return 'Unknown';
    if (author.startsWith('[SAMPLE]') || author.includes('example.test')) {
      return 'From scanned data';
    }
    return author;
  };

  const renderContent = () => {
    switch (nodeData.type) {
      case 'incident':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-destructive uppercase mb-1">
                  {nodeData.severity?.toUpperCase() || 'Critical'} Incident
                </div>
                <h3 className="text-xl font-bold text-foreground">{nodeData.title || 'Incident'}</h3>
                <div className="text-sm text-muted-foreground mt-1">{nodeData.id}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Status</div>
                <div className="font-medium text-sm text-foreground capitalize">{nodeData.status || 'Open'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Severity</div>
                <div className="font-medium text-sm text-destructive uppercase">{nodeData.severity || 'Unknown'}</div>
              </div>
              {nodeData.started_at && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Started</div>
                  <div className="font-medium text-sm text-foreground">
                    {new Date(nodeData.started_at).toLocaleString()}
                  </div>
                </div>
              )}
              {nodeData.impact && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Impact</div>
                  <div className="font-medium text-sm text-destructive">{nodeData.impact}</div>
                </div>
              )}
            </div>

            {nodeData.description && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Description</div>
                <p className="text-sm text-foreground leading-relaxed">{nodeData.description}</p>
              </div>
            )}

            {nodeData.affected_services && nodeData.affected_services.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Affected Services</div>
                <div className="flex flex-wrap gap-2">
                  {nodeData.affected_services.map((svc, idx) => (
                    <span key={idx} className="px-3 py-1 bg-info/10 text-info text-xs rounded-full">{svc}</span>
                  ))}
                </div>
              </div>
            )}

            {(nodeData.jira_url || nodeData.slack_url) && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Linked Resources</div>
                <div className="space-y-2">
                  {getExternalUrl(nodeData.jira_url) && (
                    <a href={nodeData.jira_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-info hover:text-info/80">
                      <FileText className="w-4 h-4" />
                      {nodeData.jira_ticket || 'View in Jira'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {getExternalUrl(nodeData.slack_url) && (
                    <a href={nodeData.slack_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-success hover:text-success/80">
                      <MessageSquare className="w-4 h-4" />
                      {nodeData.slack_channel || 'View in Slack'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {!nodeData.jira_url && !nodeData.slack_url && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Linked Resources</div>
                <p className="text-sm text-muted-foreground italic">
                  No linked resources found. Scan a repository to discover related tickets and discussions.
                </p>
              </div>
            )}
          </div>
        );

      case 'github':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <GitCommit className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-primary uppercase mb-1">GitHub Commit</div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.title || nodeData.message || 'Commit'}</h3>
                <div className="text-sm text-muted-foreground mt-1 font-mono">{nodeData.sha}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Author</div>
                <div className="font-medium text-sm text-foreground">{formatAuthor(nodeData.author)}</div>
              </div>
              {nodeData.timestamp && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Committed</div>
                  <div className="font-medium text-sm text-foreground">
                    {new Date(nodeData.timestamp).toLocaleString()}
                  </div>
                </div>
              )}
              {nodeData.service && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Service</div>
                  <div className="font-medium text-sm text-foreground">{nodeData.service}</div>
                </div>
              )}
              {nodeData.pr_number && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">PR Number</div>
                  <div className="font-medium text-sm text-primary">{nodeData.pr_number}</div>
                </div>
              )}
            </div>

            {nodeData.files_changed && nodeData.files_changed.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Files Changed</div>
                <div className="bg-secondary rounded-lg p-3 font-mono text-xs text-foreground space-y-1">
                  {nodeData.files_changed.map((file, idx) => (
                    <div key={idx}>{file}</div>
                  ))}
                </div>
              </div>
            )}

            {getExternalUrl(nodeData.github_url) && (
              <a
                href={nodeData.github_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full justify-center"
              >
                View on GitHub
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case 'rootcause':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-warning text-2xl font-bold">!</span>
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-warning uppercase mb-1">Root Cause Analysis</div>
                <h3 className="text-xl font-bold text-foreground">{nodeData.title || 'Root Cause'}</h3>
                <div className="text-sm text-muted-foreground mt-1">{nodeData.service}</div>
              </div>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="bg-warning/10 border-l-4 border-warning p-4 rounded-r-lg">
                <div className="text-2xl font-bold text-warning mb-2">
                  {nodeData.confidence ? `${Math.round(nodeData.confidence * 100)}%` : 'Analysis'} Confidence
                </div>
                <p className="text-sm text-foreground">
                  {nodeData.evidence || 'Causal inference analysis identified this as the primary root cause.'}
                </p>
              </div>
            </div>

            {(nodeData.baseline_rate !== undefined || nodeData.current_rate !== undefined) && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Metrics</div>
                <div className="space-y-2">
                  {nodeData.baseline_rate !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Baseline Rate</span>
                      <span className="font-semibold text-sm text-foreground">{nodeData.baseline_rate}%</span>
                    </div>
                  )}
                  {nodeData.current_rate !== undefined && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-foreground">Current Rate</span>
                      <span className="font-semibold text-sm text-destructive">{nodeData.current_rate}%</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'jira':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-info/10 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-info" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-info uppercase mb-1">Jira Ticket</div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.ticket_id || nodeData.key}</h3>
                <div className="text-sm text-muted-foreground mt-1">{nodeData.title || nodeData.summary}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              {nodeData.status && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Status</div>
                  <div className="inline-block px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded">{nodeData.status}</div>
                </div>
              )}
              {nodeData.priority && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Priority</div>
                  <div className="font-medium text-sm text-destructive">{nodeData.priority}</div>
                </div>
              )}
              {nodeData.assignee && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Assignee</div>
                  <div className="font-medium text-sm text-foreground">{formatAuthor(nodeData.assignee)}</div>
                </div>
              )}
              {nodeData.reporter && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Reporter</div>
                  <div className="font-medium text-sm text-foreground">{formatAuthor(nodeData.reporter)}</div>
                </div>
              )}
            </div>

            {nodeData.labels && nodeData.labels.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Labels</div>
                <div className="flex flex-wrap gap-2">
                  {nodeData.labels.filter(l => l !== 'sample').map((label, idx) => (
                    <span key={idx} className="px-2 py-1 bg-secondary text-foreground text-xs rounded">{label}</span>
                  ))}
                </div>
              </div>
            )}

            {getExternalUrl(nodeData.url) && (
              <a
                href={nodeData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full justify-center"
              >
                View in Jira
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case 'slack':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-primary uppercase mb-1">Slack Incident Thread</div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.title || nodeData.channel || '#incidents'}</h3>
                {nodeData.incident_id && (
                  <div className="text-sm font-mono text-primary mt-1">{nodeData.incident_id}</div>
                )}
              </div>
            </div>

            {/* Severity and Status badges */}
            {(nodeData.severity || nodeData.status) && (
              <div className="flex gap-2 pt-2">
                {nodeData.severity && (
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    nodeData.severity === 'critical' ? 'bg-destructive/10 text-destructive' :
                    nodeData.severity === 'high' ? 'bg-warning/10 text-warning' :
                    nodeData.severity === 'medium' ? 'bg-warning/10 text-warning' :
                    'bg-success/10 text-success'
                  }`}>
                    {nodeData.severity}
                  </span>
                )}
                {nodeData.status && (
                  <span className="px-2 py-1 bg-secondary text-foreground text-xs font-medium rounded-full">
                    {nodeData.status}
                  </span>
                )}
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Messages</div>
                <div className="font-medium text-sm text-foreground">{nodeData.message_count || 0}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Participants</div>
                <div className="font-medium text-sm text-foreground">
                  {nodeData.participants?.length || nodeData.participant_details?.length || 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Channel</div>
                <div className="font-medium text-sm text-foreground">{nodeData.channel || '#incidents'}</div>
              </div>
            </div>

            {/* Timestamps */}
            {(nodeData.created || nodeData.updated) && (
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                {nodeData.created && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Started</div>
                    <div className="font-medium text-sm text-foreground">
                      {new Date(nodeData.created).toLocaleString()}
                    </div>
                  </div>
                )}
                {nodeData.updated && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Last Activity</div>
                    <div className="font-medium text-sm text-foreground">
                      {new Date(nodeData.updated).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Participants with rich info */}
            {nodeData.participant_details && nodeData.participant_details.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Participants</div>
                <div className="flex flex-wrap gap-2">
                  {nodeData.participant_details.map((p, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-1.5 bg-secondary rounded-full"
                      title={p.email || p.username}
                    >
                      <div className="w-6 h-6 bg-primary/20 rounded-full flex items-center justify-center text-xs font-medium text-primary">
                        {(p.display_name || p.username || '?')[0].toUpperCase()}
                      </div>
                      <span className="text-sm text-foreground">{p.display_name || p.username}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fallback for simple participants array */}
            {!nodeData.participant_details && nodeData.participants && nodeData.participants.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Participants</div>
                <div className="flex flex-wrap gap-1.5">
                  {nodeData.participants.map((p, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-secondary text-foreground text-xs rounded-full">
                      {formatAuthor(p)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Key Findings */}
            {nodeData.key_findings && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Key Findings</div>
                <div className="bg-primary/5 border-l-4 border-primary p-3 text-sm text-foreground">
                  {nodeData.key_findings}
                </div>
              </div>
            )}

            {/* Linked Resources */}
            {nodeData.linked_resources && nodeData.linked_resources.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Linked Resources</div>
                <div className="space-y-2">
                  {nodeData.linked_resources.map((r, idx) => (
                    <a
                      key={idx}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span className="capitalize">{r.type.replace('_', ' ')}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Reactions */}
            {nodeData.reactions && Object.keys(nodeData.reactions).length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Reactions</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(nodeData.reactions).map(([emoji, count]) => (
                    <span key={emoji} className="px-2 py-1 bg-secondary text-foreground text-sm rounded-lg">
                      :{emoji}: <span className="font-medium">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {getExternalUrl(nodeData.url) && (
              <a
                href={nodeData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary w-full justify-center mt-4"
              >
                View in Slack
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        );

      case 'runbook':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-success" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-success uppercase mb-1">
                  Runbook {nodeData.source === 'scanned' ? '(Scanned)' : ''}
                </div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.title || nodeData.name || 'Runbook'}</h3>
              </div>
            </div>

            {nodeData.steps && nodeData.steps.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Procedure Steps</div>
                <ol className="space-y-2">
                  {nodeData.steps.map((step, idx) => (
                    <li key={idx} className="flex gap-3 text-sm text-foreground">
                      <span className="font-semibold text-success">{idx + 1}.</span>
                      <span>{step.replace('[SAMPLE] ', '')}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              {nodeData.owner && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Owner</div>
                  <div className="font-medium text-sm text-foreground">{formatAuthor(nodeData.owner)}</div>
                </div>
              )}
              {nodeData.last_updated && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Last Updated</div>
                  <div className="font-medium text-sm text-foreground">
                    {new Date(nodeData.last_updated).toLocaleDateString()}
                  </div>
                </div>
              )}
              {nodeData.quality && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Quality</div>
                  <div className="font-medium text-sm text-foreground">{nodeData.quality}</div>
                </div>
              )}
              {nodeData.file_path && (
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground mb-1">File</div>
                  <div className="font-mono text-xs text-muted-foreground">{nodeData.file_path}</div>
                </div>
              )}
            </div>
          </div>
        );

      case 'alert':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-destructive uppercase mb-1">
                  Alert {nodeData.source === 'scanned' ? '(Scanned)' : ''}
                </div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.name || nodeData.title || 'Alert'}</h3>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
              {nodeData.severity && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Severity</div>
                  <div className={`font-medium text-sm ${
                    nodeData.severity === 'critical' ? 'text-destructive' :
                    nodeData.severity === 'warning' ? 'text-warning' : 'text-foreground'
                  }`}>{nodeData.severity}</div>
                </div>
              )}
              {nodeData.source && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Source</div>
                  <div className="font-medium text-sm text-foreground">{nodeData.source}</div>
                </div>
              )}
              {nodeData.service && (
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Service</div>
                  <div className="font-medium text-sm text-foreground">{nodeData.service}</div>
                </div>
              )}
              {nodeData.file_path && (
                <div className="col-span-2">
                  <div className="text-xs text-muted-foreground mb-1">File</div>
                  <div className="font-mono text-xs text-muted-foreground">{nodeData.file_path}</div>
                </div>
              )}
            </div>
          </div>
        );

      case 'suggestedfix':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-success" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-success uppercase mb-1">Suggested Fix</div>
                <h3 className="text-lg font-bold text-foreground">{nodeData.title || 'Fix'}</h3>
              </div>
            </div>

            {nodeData.options && nodeData.options.length > 0 ? (
              <div className="pt-4 border-t border-border space-y-3">
                {nodeData.options.map((opt, idx) => (
                  <div key={idx}>
                    <div className="text-xs text-muted-foreground mb-2">{opt.title}</div>
                    <div className="bg-foreground rounded-lg p-3 font-mono text-xs text-success">
                      {opt.command}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="pt-4 border-t border-border space-y-3">
                <div>
                  <div className="text-xs text-muted-foreground mb-2">Recommended Action</div>
                  <div className="bg-foreground rounded-lg p-3 font-mono text-xs text-success">
                    {nodeData.command || 'See recommended actions for fix commands'}
                  </div>
                </div>
              </div>
            )}

            {(nodeData.recovery_time || nodeData.risk_level) && (
              <div className="pt-4 border-t border-border">
                <div className="text-xs text-muted-foreground mb-2">Estimated Impact</div>
                <div className="grid grid-cols-2 gap-3">
                  {nodeData.recovery_time && (
                    <div className="bg-success/10 rounded-lg p-3 text-center">
                      <div className="text-xs text-muted-foreground">Recovery Time</div>
                      <div className="text-lg font-bold text-success">{nodeData.recovery_time}</div>
                    </div>
                  )}
                  {nodeData.risk_level && (
                    <div className="bg-info/10 rounded-lg p-3 text-center">
                      <div className="text-xs text-muted-foreground">Risk Level</div>
                      <div className="text-lg font-bold text-info">{nodeData.risk_level}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p>Details for this node type are not available.</p>
            <pre className="mt-4 text-xs text-left bg-secondary p-4 rounded overflow-auto">
              {JSON.stringify(nodeData, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <div
        className="bg-card rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-lg font-semibold text-foreground">Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
            data-testid="close-modal"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <div className="px-6 py-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
